import { Capacitor, registerPlugin } from "@capacitor/core";
import { parseNativeImeViewport } from "./keyboard-viewport";

export interface NativeSafeArea {
  leftPx: number; topPx: number; rightPx: number; bottomPx: number;
  webViewWidthPx: number; webViewHeightPx: number;
}
const orientation = registerPlugin<{ getSafeArea(): Promise<NativeSafeArea> }>("VarnaxOrientation");

export function parseNativeSafeArea(value: unknown): NativeSafeArea | undefined {
  if (!value || typeof value !== "object") return;
  const data = value as NativeSafeArea;
  const numbers = [data.leftPx, data.topPx, data.rightPx, data.bottomPx, data.webViewWidthPx, data.webViewHeightPx];
  if (numbers.some((n) => typeof n !== "number" || !Number.isFinite(n) || n < 0)) return;
  if (data.webViewWidthPx < 1 || data.webViewHeightPx < 1
    || data.leftPx + data.rightPx >= data.webViewWidthPx || data.topPx + data.bottomPx >= data.webViewHeightPx) return;
  return data;
}

/** Use the WebView's width ratio, not devicePixelRatio or keyboard-reduced
 * height: zoom/density and already-resized IME must not double the insets. */
export function safeAreaCssPixels(area: NativeSafeArea, layoutWidth: number) {
  const ratio = Math.max(1, layoutWidth) / area.webViewWidthPx;
  return { left: area.leftPx * ratio, top: area.topPx * ratio, right: area.rightPx * ratio, bottom: area.bottomPx * ratio };
}

export function initializeSafeArea(): () => void {
  const root = document.documentElement;
  const native = Capacitor.isNativePlatform() && Capacitor.getPlatform() === "android";
  if (!native) return () => {};
  let disposed = false;
  const update = () => {
    const data = parseNativeSafeArea((window as Window & { __varnaxSafeArea?: unknown }).__varnaxSafeArea);
    const axis = screen.orientation?.type?.startsWith("landscape") ? "landscape"
      : screen.orientation?.type?.startsWith("portrait") ? "portrait"
      : window.innerWidth > window.innerHeight ? "landscape" : "portrait";
    root.dataset.safeAreaAxis = axis;
    // Reject stale pre-rotation geometry. A keyboard can reduce height; the
    // measured width must still be consistent with the current screen axis.
    // Native sends the resized safe area before its IME event. Do not depend
    // on the keyboard layout listener having updated the DOM attribute yet.
    const ime = parseNativeImeViewport((window as Window & { __varnaxImeViewport?: unknown }).__varnaxImeViewport);
    const keyboardOpen = ime ? ime.visible : root.dataset.keyboardOpen === "true";
    const measured = data && (keyboardOpen
      || (data.webViewWidthPx > data.webViewHeightPx) === (axis === "landscape"));
    root.dataset.nativeSafeArea = measured ? "measured" : "legacy";
    const insets = measured ? safeAreaCssPixels(data, window.innerWidth) : { left: 0, top: 0, right: 0, bottom: 0 };
    for (const edge of ["left", "top", "right", "bottom"] as const) root.style.setProperty(`--vn-native-safe-${edge}`, `${insets[edge]}px`);
  };
  const refresh = () => { update(); void orientation.getSafeArea().then((result) => {
    if (disposed || !parseNativeSafeArea(result)) return;
    (window as Window & { __varnaxSafeArea?: unknown }).__varnaxSafeArea = result;
    update();
  }).catch(() => { /* Older AABs receive a conservative content-only inset. */ }); };
  window.addEventListener("varnax:safe-area", update);
  window.addEventListener("varnax:ime-viewport", update);
  window.addEventListener("orientationchange", refresh);
  screen.orientation?.addEventListener("change", refresh);
  window.addEventListener("resize", update);
  refresh();
  return () => {
    disposed = true;
    window.removeEventListener("varnax:safe-area", update);
    window.removeEventListener("varnax:ime-viewport", update);
    window.removeEventListener("orientationchange", refresh);
    screen.orientation?.removeEventListener("change", refresh);
    window.removeEventListener("resize", update);
    delete root.dataset.nativeSafeArea; delete root.dataset.safeAreaAxis;
    for (const edge of ["left", "top", "right", "bottom"]) root.style.removeProperty(`--vn-native-safe-${edge}`);
  };
}
